// ============================================
// Firebase Auth Service
// Сервис аутентификации Firebase
// ============================================

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile,
  getAuth
} from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc, getDocs, query, collection, orderBy, getFirestore } from 'firebase/firestore';
import { generateRandomAvatarUrl } from '../avatarUtils';
import { getFirebaseApp, isFirebaseConfigured } from '../firebase';
import type { User, UserRole, FirestoreUser } from '../../types/auth';

// Генерирует уникальный ID
const generateId = () => Math.random().toString(36).substring(2, 15) + Date.now().toString(36);

// Хранит текущего пользователя для getCurrentUserId
let currentUserId: string | null = null;

/**
 * Получает ID текущего аутентифицированного пользователя
 * @returns ID пользователя или null
 */
export const getCurrentUserId = (): string | null => {
  return currentUserId;
};

// Lazy getters для auth и db - избегаем race condition
const getAuthInstance = () => {
  try {
    const app = getFirebaseApp();
    return getAuth(app);
  } catch {
    return null;
  }
};

const getDbInstance = () => {
  try {
    const app = getFirebaseApp();
    return getFirestore(app);
  } catch {
    return null;
  }
};

// Проверяет доступность Firebase
const isFirebaseAvailable = (): boolean => {
  try {
    const hasWindow = typeof window !== 'undefined';
    const isConfigured = isFirebaseConfigured();
    const auth = getAuthInstance();
    const db = getDbInstance();
    
    // DEBUG: Detailed logging
    console.log('[isFirebaseAvailable] Debug:', {
      hasWindow,
      isConfigured,
      hasAuth: !!auth,
      hasDb: !!db
    });
    
    return hasWindow && isConfigured && !!auth && !!db;
  } catch (error) {
    console.error('[isFirebaseAvailable] Error:', error);
    return false;
  }
};

// Вход по email и паролю
export const signIn = async (email: string, password: string): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseAvailable()) {
    return { user: null, error: 'Firebase not configured' };
  }

  const auth = getAuthInstance();
  const db = getDbInstance();
  if (!auth || !db) {
    return { user: null, error: 'Firebase not initialized' };
  }

  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email || email,
          displayName: userData.displayName || email.split('@')[0],
          role: userData.role || 'viewer',
          emailVerified: userData.emailVerified ?? false,
          createdAt: userData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          settings: userData.settings || { theme: 'dark' },
          avatarUrl: userData.avatarUrl || undefined
        },
        error: null
      };
    }
    
    return {
      user: {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        displayName: email.split('@')[0],
        role: 'viewer',
        emailVerified: firebaseUser.emailVerified,
        createdAt: new Date().toISOString(),
        settings: { theme: 'dark' },
        avatarUrl: undefined
      },
      error: null
    };
  } catch (error: unknown) {
    const err = error as Error;
    return { user: null, error: err.message || 'Failed to sign in' };
  }
};

// Регистрация нового пользователя
export const signUp = async (email: string, password: string, displayName: string, role: string = 'viewer'): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseAvailable()) {
    return { user: null, error: 'Firebase not configured' };
  }

  const auth = getAuthInstance();
  const db = getDbInstance();
  if (!auth || !db) {
    return { user: null, error: 'Firebase not initialized' };
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    await updateProfile(firebaseUser, { displayName });
    
    // Generate default avatar URL using predefined styles
    const defaultAvatarUrl = generateRandomAvatarUrl(email);
    
    const newUser: Omit<FirestoreUser, 'id'> = {
      email,
      displayName,
      role: role as UserRole,
      emailVerified: false,
      createdAt: new Date().toISOString(),
      settings: { theme: 'dark' },
      avatarUrl: defaultAvatarUrl
    };
    
    await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
    
    return {
      user: {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        displayName,
        role: role as UserRole,
        emailVerified: false,
        createdAt: new Date().toISOString(),
        settings: { theme: 'dark' },
        avatarUrl: defaultAvatarUrl
      },
      error: null
    };
  } catch (error: unknown) {
    const err = error as Error;
    return { user: null, error: err.message || 'Failed to sign up' };
  }
};

// Выход из системы
export const signOut = async (): Promise<void> => {
  if (!isFirebaseAvailable()) return;
  
  const auth = getAuthInstance();
  if (!auth) return;
  
  try {
    await firebaseSignOut(auth);
    currentUserId = null;
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

// Подписка на изменения состояния аутентификации
export const onAuthStateChanged = (callback: (user: User | null) => void) => {
  if (!isFirebaseAvailable()) {
    callback(null);
    return () => {};
  }

  const auth = getAuthInstance();
  const db = getDbInstance();
  if (!auth || !db) {
    callback(null);
    return () => {};
  }

  return firebaseOnAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      currentUserId = firebaseUser.uid;
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        callback({
          id: firebaseUser.uid,
          email: firebaseUser.email || userData.email || '',
          displayName: userData.displayName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          role: userData.role || 'viewer',
          emailVerified: userData.emailVerified ?? firebaseUser.emailVerified ?? false,
          createdAt: userData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          settings: userData.settings || { theme: 'dark' },
          avatarUrl: userData.avatarUrl || undefined
        });
      } else {
        callback({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          role: 'viewer',
          emailVerified: firebaseUser.emailVerified,
          createdAt: new Date().toISOString(),
          settings: { theme: 'dark' },
          avatarUrl: undefined
        });
      }
    } else {
      callback(null);
    }
  });
};

// DEV вход - для разработки
export const devLogin = (role: Exclude<UserRole, 'pending'>): { user: User | null; error: string | null } => {
  if (!import.meta.env.DEV) {
    return { user: null, error: 'DEV login is only available in development mode' };
  }

  const roleNames: Record<Exclude<UserRole, 'pending'>, string> = {
    analyst: 'Аналітик',
    developer: 'Розробник',
    viewer: 'Смотритель'
  };
  
  const displayName = roleNames[role];
  
  if (displayName) {
    // Generate dev avatar URL using predefined styles
    const devAvatarUrl = generateRandomAvatarUrl(role);
    return {
      user: {
        id: `dev_${role}_${Date.now()}`,
        email: `${role}@dev.local`,
        displayName,
        role,
        emailVerified: true,
        createdAt: new Date().toISOString(),
        settings: { theme: 'dark' },
        avatarUrl: devAvatarUrl
      },
      error: null
    };
  }
  
  return { user: null, error: 'Invalid role for DEV login' };
};

// Создание пользователя (только для аналитиков)
export const createUser = async (
  email: string, 
  password: string, 
  displayName: string, 
  role: UserRole,
  analystUser: User
): Promise<{ user: User | null; error: string | null }> => {
  if (analystUser.role !== 'analyst') {
    return { user: null, error: 'Only analysts can create users' };
  }

  if (!isFirebaseAvailable()) {
    return { user: null, error: 'Firebase not configured' };
  }

  const auth = getAuthInstance();
  const db = getDbInstance();
  if (!auth || !db) {
    return { user: null, error: 'Firebase not initialized' };
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;
    
    // Generate default avatar URL using predefined styles
    const defaultAvatarUrl = generateRandomAvatarUrl(email);
    
    const firestoreUser: Omit<FirestoreUser, 'id'> = {
      email,
      displayName,
      role,
      emailVerified: false,
      createdAt: new Date().toISOString(),
      settings: { theme: 'dark' },
      avatarUrl: defaultAvatarUrl
    };
    
    await setDoc(doc(db, 'users', firebaseUser.uid), firestoreUser);
    await updateProfile(firebaseUser, { displayName });
    
    return {
      user: {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        displayName,
        role,
        emailVerified: false,
        createdAt: new Date().toISOString(),
        settings: { theme: 'dark' },
        avatarUrl: defaultAvatarUrl
      },
      error: null
    };
  } catch (error: unknown) {
    const err = error as Error;
    return { user: null, error: err.message || 'Failed to create user' };
  }
};

// Получение всех пользователей
export const getUsers = async (analystUser: User): Promise<FirestoreUser[]> => {
  if (analystUser.role !== 'analyst') {
    return [];
  }

  if (!isFirebaseAvailable()) {
    return [];
  }

  const db = getDbInstance();
  if (!db) return [];

  try {
    const querySnapshot = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
    const users: FirestoreUser[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({
        id: doc.id,
        email: data.email || '',
        displayName: data.displayName || '',
        role: data.role || 'viewer',
        emailVerified: data.emailVerified ?? false,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        settings: data.settings || { theme: 'dark' },
        avatarUrl: data.avatarUrl || undefined
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    return [];
  }
};

// Удаление пользователя
export const deleteUser = async (userId: string, analystUser: User): Promise<{ success: boolean; error: string | null }> => {
  if (analystUser.role !== 'analyst') {
    return { success: false, error: 'Only analysts can delete users' };
  }

  if (!isFirebaseAvailable()) {
    return { success: false, error: 'Firebase not configured' };
  }

  const db = getDbInstance();
  if (!db) {
    return { success: false, error: 'Firebase not initialized' };
  }

  try {
    await deleteDoc(doc(db, 'users', userId));
    return { success: true, error: null };
  } catch (error: unknown) {
    const err = error as Error;
    return { success: false, error: err.message || 'Failed to delete user' };
  }
};

export { isFirebaseAvailable, generateId };
